import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { TranslocoService } from '@ngneat/transloco';
import { RxActions, rxActions } from '@rx-angular/state/actions';
import { VesselReportsState } from '@vessel-reports/state/vessel-reports.state';
import { catchError, exhaustMap, filter, merge, of, switchMap, tap } from 'rxjs';
import { VesselReportsService } from './vessel-reports.service';
import { MovementDetail, VesselReport, VesselReportPort, VesselSchedule } from './vessel-reports.types';

interface IVesselReportsAction {
  getVesselReports: {
    type?: string;
    queryParams?: Params;
    vesselId?: string;
  };
  getVesselReportsSuccess: void;
  getVesselReportById: {
    id: string;
  };
  getVesselReportByIdSuccess: {
    vesselReport: VesselReport;
  };
  getPreviousVesselReport: {
    vesselId: string;
    type: number;
  };
  getPreviousVesselReportSuccess: {
    vesselReport: VesselReport;
  };
  createVesselReport: {
    vesselReport: VesselReport;
    activatedRoute: ActivatedRoute;
  };
  createVesselReportSuccess: void;
  updateVesselReport: {
    id: string;
    vesselReport: VesselReport;
  };
  updateVesselReportSuccess: {
    vesselReport: VesselReport;
  };
  updateVesselReportDetails: {
    vesselReportDetails: any;
    vesselId: string;
    isCreate?: boolean;
  };
  updateVesselReportDetailsSuccess: void;
  submitVesselReport: {
    vesselId: string;
  };
  submitVesselReportSuccess: void;
  deleteVesselReport: {
    id: string;
    type: string;
    activatedRoute: ActivatedRoute;
    vesselId?: string;
  };
  deleteVesselReportSuccess: void;
  getVesselReportPorts: {
    type?: string;
    queryParams?: Params;
  };
  getVesselReportPortsSuccess: {
    pagination: any;
    ports: VesselReportPort[];
  };
  uploadAttachment: {
    id: string;
    attachmentTypeId: string;
    file: File;
  };
  uploadAttachmentSuccess: { vesselReport: VesselReport };
  removeAttachment: {
    vesselReportId: string;
    id: string;
    attachmentTypeId: string;
  };
  removeAttachmentSuccess: { vesselReport: VesselReport };

  getVesselSchedule: {
    startDate: number;
    endDate: number;
  };
  getVesselScheduleSuccess: {
    schedule: VesselSchedule;
  };
  getMovementDetail: {
    movementId: string;
  };
  getMovementDetailSuccess: {
    detail: MovementDetail;
  };
  selectEtaFilter: {
    type: 'all' | '3m' | 'yd' | 'custom' | null;
  };
}

@Injectable({
  providedIn: 'root'
})
export class VesselReportsAction {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  actions: RxActions<IVesselReportsAction> = rxActions<IVesselReportsAction>();
  private readonly _vesselService: VesselReportsService = inject(VesselReportsService);
  private readonly _state: VesselReportsState = inject(VesselReportsState);
  private readonly _popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToGetVesselReports$();
    this._subscribeToGetVesselReportByIdSuccess$();
    this._subscribeToGetPreviousVesselReportSuccess$();
    this._subscribeToUpdateVesselReportSuccess$();
    this._subscribeToUploadOrRemoveCompanyAttachmentSuccess$();
    this._subscribeToGetVesselReportPortsSuccess$();
    this._subscribeToGetVesselSchedule$();
    this._subscribeToGetMovementDetail$();
    this._subscribeToSelectEtaFilter$();
    this._subscribeToDeleteReport$();
    this._subscribeToGetPortReports$();
  }

  private _subscribeToGetVesselReports$() {
    type APIResponse = UnwrapReturnObs<typeof this._vesselService.getVesselReports>;
    this.actions.getVesselReports$
      .pipe(
        switchMap((payload) =>
          this._vesselService.getVesselReports(payload.type, payload?.queryParams).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: ({ pagination, data }: any) => {
          this._state.set({
            pagination: pagination,
            vesselReports: data
          });
          this.actions.getVesselReportsSuccess();
        }
      });
  }

  private _subscribeToGetVesselReportByIdSuccess$() {
    this.actions.getVesselReportByIdSuccess$.subscribe({
      next: ({ vesselReport }: any) => {
        this._state.set({
          vesselReport: vesselReport
        });
      }
    });
  }

  private _subscribeToGetPreviousVesselReportSuccess$() {
    this.actions.getPreviousVesselReportSuccess$.subscribe({
      next: ({ vesselReport }: any) => {
        this._state.set({
          previousVesselReport: vesselReport
        });
      }
    });
  }

  private _subscribeToGetVesselReportPortsSuccess$() {
    this.actions.getVesselReportPortsSuccess$.subscribe({
      next: ({ pagination, ports }: any) => {
        this._state.set({
          vesselReportPorts: ports,
          vesselReportPortsPagination: pagination
        });
      }
    });
  }

  private _subscribeToUpdateVesselReportSuccess$() {
    this.actions.updateVesselReportSuccess$.subscribe({
      next: ({ vesselReport }: any) => {
        this._state.set({
          vesselReports: this._state.vesselReports().map((item) => (item.id == vesselReport.id ? vesselReport : item)),
          vesselReport: this._state.vesselReport()?.id == vesselReport.id ? vesselReport : this._state.vesselReport()
        });
      }
    });
  }

  private _subscribeToUploadOrRemoveCompanyAttachmentSuccess$() {
    merge(this.actions.uploadAttachmentSuccess$, this.actions.removeAttachmentSuccess$).subscribe({
      next: ({ vesselReport }: any) => {
        this._state.set({
          vesselReports: this._state.vesselReports().map((item) => (item.id == vesselReport.id ? vesselReport : item)),
          vesselReport: this._state.vesselReport()?.id == vesselReport.id ? vesselReport : this._state.vesselReport()
        });
      }
    });
  }

  private _subscribeToGetVesselSchedule$() {
    type APIResponse = UnwrapReturnObs<typeof this._vesselService.getVesselSchedule>;
    this.actions.getVesselSchedule$
      .pipe(
        switchMap((payload) =>
          this._vesselService.getVesselSchedule(payload.startDate, payload.endDate).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: (schedule) => {
          this._state.set({
            vesselSchedule: schedule
          });

          this.actions.getVesselScheduleSuccess({ schedule: schedule });
        }
      });
  }

  private _subscribeToGetMovementDetail$() {
    this._state.set({
      selectedMovementDetail: null
    });

    type APIResponse = UnwrapReturnObs<typeof this._vesselService.getMovementDetail>;
    this.actions.getMovementDetail$
      .pipe(
        switchMap((payload) =>
          this._vesselService.getMovementDetail(payload.movementId).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: (detail) => {
          this._state.set({
            selectedMovementDetail: detail
          });

          this.actions.getMovementDetailSuccess({ detail: detail });
        }
      });
  }

  private _subscribeToSelectEtaFilter$(): void {
    this.actions.onSelectEtaFilter((payload$) =>
      payload$.pipe(
        tap((payload) => {
          this._state.set({
            etaFilter: payload.type
          });
        })
      )
    );
  }

  private _subscribeToDeleteReport$() {
    this.actions.onDeleteVesselReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._vesselService.deleteVesselReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._vesselService.deleteVesselReport(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.vessel_report_deleted_successfully')
        });
        this.actions.deleteVesselReportSuccess();
      }
    );
  }

  private _subscribeToGetPortReports$() {
    type APIResponse = UnwrapReturnObs<typeof this._vesselService.getVesselReports>;
    this.actions.getVesselReportPorts$
      .pipe(
        switchMap((payload) =>
          this._vesselService.getVesselReports(payload.type, payload?.queryParams).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: ({ pagination, data }: any) => {
          this._state.set({
            pagination: pagination,
            vesselReports: data
          });
          this.actions.getVesselReportPortsSuccess(data);
        }
      });
  }
}
